// Stage2.jsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

const panelOptions = [
  {
    name: "Mono PERC",
    efficiency: "21%",
    warranty: "25 Years",
    compatible: ["onGrid", "offGrid"],
  },
  {
    name: "Polycrystalline",
    efficiency: "17%",
    warranty: "20 Years",
    compatible: ["onGrid"],
  },
];

const inverterOptions = [
  {
    type: "String Inverter",
    recommendedFor: "On-Grid",
    brand: "Sungrow",
    compatible: ["onGrid"],
  },
  {
    type: "Hybrid Inverter",
    recommendedFor: "Off-Grid",
    brand: "Luminous",
    compatible: ["offGrid"],
  },
];

const batteryOptions = [
  {
    capacity: "5kWh",
    type: "Lithium",
    cycles: "3000+",
    compatible: ["offGrid"],
  },
  {
    capacity: "7.5kWh",
    type: "Lithium",
    cycles: "5000+",
    compatible: ["offGrid"],
  },
];

const wiringOptions = [
  {
    type: "FR Copper",
    description: "Flame-retardant copper wires ideal for Rajasthan heat",
    compatible: ["onGrid", "offGrid"],
  },
  {
    type: "Aluminium",
    description: "Cheaper but less durable and heat-resistant",
    compatible: ["onGrid"],
  },
];

const structureOptions = [
  {
    material: "Galvanized Steel",
    note: "Corrosion resistant, long-lasting",
    compatible: ["onGrid", "offGrid"],
  },
  {
    material: "Aluminium",
    note: "Lightweight, moderate strength",
    compatible: ["offGrid"],
  },
];

const meterOptions = [
  {
    label: "Apply for Net Metering",
    description: "Sell excess energy back to the grid.",
  },
  {
    label: "Include Smart Monitor",
    description: "Track daily solar output and usage.",
  },
];

const Stage2 = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const isOffGrid = state?.type === "offGrid";
  const systemType = isOffGrid ? "offGrid" : "onGrid";
  const [selectedMeters, setSelectedMeters] = useState([
    "Apply for Net Metering",
  ]);

  const [selectedComponents, setSelectedComponents] = useState({
    panel: panelOptions[0].name,
    inverter: inverterOptions.find((inv) => inv.compatible.includes(systemType))
      ?.type,
    battery: isOffGrid ? batteryOptions[0].capacity : null,
    wiring: wiringOptions[0].type,
    structure: structureOptions[0].material,
  });

  const handleSummarize = (stage2Data) => {
    const stage1Data = state;

    navigate("../stage3", {
      state: {
        ...stage1Data,
        ...stage2Data,
      },
    });
  };

  return (
    <Box px={2} maxWidth="1000px" mx="auto">
      <Typography variant="h4" fontWeight={600} textAlign="center" gutterBottom>
        Choose Your Personalized Kit
        <Chip
          label={isOffGrid ? "Off-Grid System" : "On-Grid System"}
          color={isOffGrid ? "secondary" : "primary"}
          size="small"
          sx={{ ml: 2 }}
        />
      </Typography>

      <div
        style={{
          display: "flex",
          gap: "1.5rem",
          alignItems: "stretch", // ensures children stretch to same height
        }}
      >
        <div style={{ flex: 1 }}>
          <Paper
            elevation={2}
            sx={{
              height: "100%", // ensure full stretch
              p: 3,
              borderRadius: 4,
              bgcolor: "#fcfcfc",
              display: "flex",
              flexDirection: "column", // maintain layout inside
            }}
          >
            {/* Content for Recommended Components */}
            <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <div>🔧 Recommended Components</div>
                <button
                  onClick={()=>handleSummarize(selectedComponents)}
                  style={{
                    backgroundColor: "#1976d2", // MUI primary.main
                    color: "#fff",
                    border: "none",
                    padding: "6px 16px",
                    borderRadius: "8px",
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    transition: "background-color 0.3s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#1565c0")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "#1976d2")
                  }
                >
                  Summarise
                </button>
              </div>
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Helper function to render Select blocks */}
            {[
              {
                label: "Solar Panel",
                stateKey: "panel",
                options: panelOptions,
                getOptionLabel: (p) =>
                  `${p.name} - ${p.efficiency}, ${p.warranty} Warranty`,
              },
              {
                label: "Inverter",
                stateKey: "inverter",
                options: inverterOptions,
                getOptionLabel: (i) =>
                  `${i.type} - ${i.brand} (${i.recommendedFor})`,
              },
              ...(isOffGrid
                ? [
                    {
                      label: "Battery (Backup)",
                      stateKey: "battery",
                      options: batteryOptions,
                      getOptionLabel: (b) =>
                        `${b.capacity} - ${b.type} (${b.cycles} cycles)`,
                    },
                  ]
                : []),
              {
                label: "Wiring",
                stateKey: "wiring",
                options: wiringOptions,
                getOptionLabel: (w) => `${w.type} - ${w.description}`,
              },
              {
                label: "Mounting Structure",
                stateKey: "structure",
                options: structureOptions,
                getOptionLabel: (s) => `${s.material} - ${s.note}`,
              },
            ].map((field, i) => (
              <Box
                key={field.label}
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#f5f5f5",
                  boxShadow: "inset 0 0 0 1px #e0e0e0",
                }}
              >
                <FormControl fullWidth size="small">
                  <InputLabel>{`Select ${
                    field.label.split(" ")[0]
                  }`}</InputLabel>
                  <Select
                    value={selectedComponents[field.stateKey]}
                    onChange={(e) =>
                      setSelectedComponents({
                        ...selectedComponents,
                        [field.stateKey]: e.target.value,
                      })
                    }
                    label={`Select ${field.label.split(" ")[0]}`}
                    sx={{
                      maxWidth: "28rem",
                      minWidth: 0, // ensure it doesn't force extra width
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap", // optional, avoids wrapping inside options
                    }}
                  >
                    {field.options
                      .filter((opt) => opt.compatible.includes(systemType))
                      .map((opt) => (
                        <MenuItem
                          key={
                            opt.name || opt.type || opt.capacity || opt.material
                          }
                          value={
                            opt.name || opt.type || opt.capacity || opt.material
                          }
                        >
                          {field.getOptionLabel(opt)}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
            ))}

            {/* Metering Options */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "#f0f4ff",
                boxShadow: "inset 0 0 0 1px #d0dfff",
              }}
            >
              <Typography fontWeight={600} gutterBottom>
                Metering Options
              </Typography>
              {meterOptions.map((m, index) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      checked={selectedMeters.includes(m.label)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedMeters((prev) =>
                          checked
                            ? [...prev, m.label]
                            : prev.filter((label) => label !== m.label)
                        );
                      }}
                    />
                  }
                  label={
                    <Tooltip title={m.description} placement="right">
                      <span>{m.label}</span>
                    </Tooltip>
                  }
                  sx={{ mb: 1 }}
                />
              ))}
            </Box>
          </Paper>
        </div>

        <div style={{ flex: 1 }}>
          <Paper
            elevation={3}
            sx={{
              height: "100%", // ensure full stretch
              p: 4,
              borderRadius: 4,
              bgcolor: "#f9fafb",
              display: "flex",
              flexDirection: "column", // maintain layout inside
            }}
          >
            {/* Content for System Overview */}
            <Typography variant="h6" gutterBottom>
              🔍 System Overview
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              {/* Top Stats */}
              {[
                {
                  label: "System Type",
                  value: isOffGrid ? "Off-Grid" : "On-Grid",
                },
                { label: "Capacity", value: `${state?.capacity} kW` },
                { label: "Rooftop Area", value: `${state?.area} sq.ft` },
                { label: "Structure Height", value: state?.structureHeight },
              ].map((item, index) => (
                <Grid item xs={6} key={index}>
                  <Typography variant="body2" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography fontWeight={600}>{item.value}</Typography>
                </Grid>
              ))}

              <Grid container spacing={3}>
                {/* Average Monthly Bill */}
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Current Electricity Bill
                  </Typography>
                  <Typography
                    fontWeight={700}
                    color="success.main"
                    fontSize="1.2rem"
                  >
                    ₹{state?.billAmount}
                  </Typography>
                </Grid>

                {/* Monthly Savings */}
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Approx. Monthly Savings
                  </Typography>
                  <Typography
                    fontWeight={700}
                    color="success.main"
                    fontSize="1.2rem"
                  >
                    ₹{state?.savings?.monthly.toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>

              {/* Component Details */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom>
                  🧩 Selected Components
                </Typography>

                {[
                  { label: "Panel", value: selectedComponents.panel },
                  { label: "Inverter", value: selectedComponents.inverter },
                  isOffGrid && {
                    label: "Battery",
                    value: selectedComponents.battery,
                  },
                  { label: "Wiring", value: selectedComponents.wiring },
                  { label: "Structure", value: selectedComponents.structure },
                ]
                  .filter(Boolean)
                  .map((comp, index) => (
                    <Typography key={index} fontSize={14} sx={{ mb: 0.5 }}>
                      • <strong>{comp.label}:</strong> {comp.value}
                    </Typography>
                  ))}
              </Grid>

              {/* Metering Options */}
              {selectedMeters?.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    🧮 Metering Options
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {selectedMeters.map((label, i) => (
                      <Chip key={i} label={label} size="small" />
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        </div>
      </div>
    </Box>
  );
};

export default Stage2;
